import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

const notesPopoverStyles = `
  .rt-notes-popover-wrapper {
    display: inline-block !important;
  }
  .rt-notes-popover-wrapper .rt-form-action-btn,
  .rt-notes-popover-wrapper .is-notes-btn,
  .rt-notes-popover-wrapper button {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 6px !important;
    padding: 5px 12px !important;
    height: 30px !important;
    min-height: 30px !important;
    box-sizing: border-box !important;
    border-radius: 0 !important;
    font-family: "Open Sans", sans-serif !important;
    font-size: 13px !important;
    font-weight: 500 !important;
    color: #1e293b !important;
    cursor: pointer !important;
    border: 1px solid #cbd5e1 !important;
    background: #ffffff !important;
    box-shadow: 0 2px 5px rgba(15, 23, 42, 0.06) !important;
    transition: all 0.15s ease !important;
    outline: none !important;
  }
  .rt-notes-popover-wrapper .rt-form-action-btn:hover,
  .rt-notes-popover-wrapper .is-notes-btn:hover,
  .rt-notes-popover-wrapper button:hover {
    background: #f8fafc !important;
    color: #0f172a !important;
    border-color: #94a3b8 !important;
    box-shadow: 0 3px 8px rgba(15, 23, 42, 0.1) !important;
  }
  .rt-premium-popover {
    background: #ffffff !important;
    border: 1px solid #cbd5e1 !important;
    border-radius: 0 !important;
    box-shadow: 0 10px 15px -3px rgba(0,0,0,0.08), 0 4px 6px -2px rgba(0,0,0,0.04) !important;
    z-index: 99999 !important;
    width: 250px !important;
    font-family: "Open Sans", sans-serif !important;
    padding: 0 !important;
  }
  .rt-premium-popover-content {
    padding: 12px 14px !important;
  }
  .rt-premium-popover-title {
    font-weight: 600 !important;
    font-size: 13px !important;
    color: #0f172a !important;
    margin: 0 0 6px 0 !important;
    border-bottom: 1px solid #f1f5f9 !important;
    padding-bottom: 6px !important;
    display: flex !important;
    align-items: center !important;
    gap: 6px !important;
  }
  .rt-premium-popover-body {
    font-size: 12.5px !important;
    color: #475569 !important;
    line-height: 1.5 !important;
    max-height: 180px !important;
    overflow-y: auto !important;
    white-space: normal !important;
    word-break: break-word !important;
    scrollbar-width: thin !important;
    scrollbar-color: #cbd5e1 #f8fafc !important;
  }

  /* Portal positioning arrow styles */
  .rt-portal-popover.placement-top .rt-premium-popover-arrow {
    position: absolute !important;
    top: 100% !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
    width: 0 !important;
    height: 0 !important;
    border-left: 6px solid transparent !important;
    border-right: 6px solid transparent !important;
    border-top: 6px solid #cbd5e1 !important;
  }
  .rt-portal-popover.placement-top .rt-premium-popover-arrow::after {
    content: "" !important;
    position: absolute !important;
    top: -7px !important;
    left: -6px !important;
    width: 0 !important;
    height: 0 !important;
    border-left: 6px solid transparent !important;
    border-right: 6px solid transparent !important;
    border-top: 6px solid #ffffff !important;
  }

  .rt-portal-popover.placement-bottom .rt-premium-popover-arrow {
    position: absolute !important;
    bottom: 100% !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
    width: 0 !important;
    height: 0 !important;
    border-left: 6px solid transparent !important;
    border-right: 6px solid transparent !important;
    border-bottom: 6px solid #cbd5e1 !important;
  }
  .rt-portal-popover.placement-bottom .rt-premium-popover-arrow::after {
    content: "" !important;
    position: absolute !important;
    top: 1px !important;
    left: -6px !important;
    width: 0 !important;
    height: 0 !important;
    border-left: 6px solid transparent !important;
    border-right: 6px solid transparent !important;
    border-bottom: 6px solid #ffffff !important;
  }
`;

const NotesPopover = ({
  notes,
  placement = "top",
  buttonLabel = "View",
  buttonClassName = "rt-form-action-btn is-notes-btn"
}) => {
  const [show, setShow] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const popoverRef = useRef(null);

  const togglePopover = (e) => {
    e.stopPropagation();
    const nextShow = !show;
    if (nextShow) {
      // Close other NotesPopover instances before opening this one
      document.dispatchEvent(new CustomEvent("notespopover:close"));
    }
    setShow(nextShow);
  };

  useEffect(() => {
    if (!show) return;

    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setCoords({
          left: rect.left + rect.width / 2 + window.scrollX,
          top: placement === "bottom"
            ? rect.bottom + window.scrollY + 8
            : rect.top + window.scrollY - 8
        });
      }
    };

    updatePosition();

    const handleOutsideClick = (e) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target) &&
        popoverRef.current && !popoverRef.current.contains(e.target)
      ) {
        setShow(false);
      }
    };

    const handleCloseAll = () => {
      setShow(false);
    };

    // Close when the user scrolls the page or any parent container (like the table)
    const handleScroll = () => {
      setShow(false);
    };

    document.addEventListener("click", handleOutsideClick);
    document.addEventListener("notespopover:close", handleCloseAll);
    window.addEventListener("scroll", handleScroll, true); // true to capture scroll on any element
    window.addEventListener("resize", handleScroll);

    return () => {
      document.removeEventListener("click", handleOutsideClick);
      document.removeEventListener("notespopover:close", handleCloseAll);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleScroll);
    };
  }, [show, placement]);

  return (
    <div
      className="rt-notes-popover-wrapper"
      style={{ display: "inline-block" }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        ref={buttonRef}
        className={buttonClassName}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
          padding: "5px 12px",
          height: "30px",
          minHeight: "30px",
          boxSizing: "border-box",
          borderRadius: 0,
          fontFamily: "inherit",
          fontSize: "13px",
          fontWeight: 500,
          color: "#1e293b",
          cursor: "pointer",
          border: "1px solid #cbd5e1",
          background: "#ffffff",
          boxShadow: "0 2px 5px rgba(15, 23, 42, 0.06)",
          outline: "none"
        }}
        type="button"
        onClick={togglePopover}
      >
        <i className="fa fa-commenting-o" aria-hidden="true" /> {buttonLabel}
      </button>

      {show && createPortal(
        <div
          ref={popoverRef}
          className={`rt-premium-popover rt-portal-popover placement-${placement}`}
          style={{
            position: "absolute",
            left: `${coords.left}px`,
            top: `${coords.top}px`,
            transform: placement === "bottom" ? "translateX(-50%)" : "translateX(-50%) translateY(-100%)",
            zIndex: 99999
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <style>{notesPopoverStyles}</style>
          <div className="rt-premium-popover-arrow" />
          <div className="rt-premium-popover-content">
            <div className="rt-premium-popover-title">
              <i className="fa fa-commenting-o" aria-hidden="true" style={{ color: "#2563eb" }} />
              Notes
            </div>
            <div className="rt-premium-popover-body">{notes}</div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default NotesPopover;
